import { useCallback, useEffect, useState } from "react";
import { api, pushApi } from "./api.ts";
import { isIos, usePwa } from "./pwa.ts";
import { keyToBytes, lastPlayedDay, mirrorReminderState, reminderAvailability, type ReminderAvailability } from "./reminders.ts";
import { useStore } from "./store.ts";

const hasPushApi = () => import.meta.env.PROD && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const permission = (): NotificationPermission | "unsupported" => ("Notification" in window ? Notification.permission : "unsupported");
const timeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** The server's push key, or null if reminders aren't switched on there. Read once. */
function usePushKey(): string | null {
  const [key, setKey] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.config().then((c) => !cancelled && setKey(c.pushPublicKey ?? null)).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return key;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/** Tell the server where to send, in which zone and when, and the day we last played. Safe to repeat. */
async function checkIn(sub: PushSubscription, hour: number): Promise<void> {
  const { streaks } = useStore.getState();
  await pushApi.subscribe({ endpoint: sub.endpoint, tz: timeZone(), hour, lastPlayed: lastPlayedDay(streaks) });
  await mirrorReminderState(streaks);
}

const message = (e: unknown) => (e instanceof Error && e.message ? e.message : "Couldn't turn reminders on. Please try again.");

export interface ReminderControls {
  availability: ReminderAvailability;
  on: boolean;
  hour: number;
  busy: boolean;
  error: string | null;
  testState: "idle" | "sending" | "sent";
  turnOn: () => Promise<void>;
  turnOff: () => Promise<void>;
  setHour: (hour: number) => Promise<void>;
  sendTest: () => Promise<void>;
}

export function useReminder(): ReminderControls {
  const key = usePushKey();
  const standalone = usePwa((s) => s.standalone);
  const { on, hour } = useStore((s) => s.reminder);
  const setReminder = useStore((s) => s.setReminder);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testState, setTestState] = useState<"idle" | "sending" | "sent">("idle");
  const [perm, setPerm] = useState(permission);

  const availability = reminderAvailability({
    serverEnabled: key !== null,
    hasPushApi: hasPushApi(),
    ios: isIos({ userAgent: navigator.userAgent, platform: navigator.platform, maxTouchPoints: navigator.maxTouchPoints }),
    standalone,
    permission: perm,
  });

  const turnOn = useCallback(async () => {
    if (!key) return;
    setBusy(true);
    setError(null);
    try {
      // Asked straight from the tap: iPhone only shows the permission prompt in response to a gesture.
      const answer = await Notification.requestPermission();
      setPerm(answer);
      if (answer !== "granted") return;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        try {
          sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyToBytes(key) });
        } catch (e) {
          if (!(e instanceof DOMException && e.name === "InvalidStateError")) throw e;
          // A subscription made with an older key: replace it.
          await (await reg.pushManager.getSubscription())?.unsubscribe();
          sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyToBytes(key) });
        }
      }
      await checkIn(sub, useStore.getState().reminder.hour);
      setReminder({ on: true });
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }, [key, setReminder]);

  const turnOff = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const sub = await currentSubscription();
      // Local first: even if the server can't be reached, this phone stops receiving. The server drops the row when the push service says it's gone.
      setReminder({ on: false });
      if (sub) {
        await pushApi.unsubscribe(sub.endpoint).catch(() => undefined);
        await sub.unsubscribe();
      }
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }, [setReminder]);

  const changeHour = useCallback(
    async (next: number) => {
      setReminder({ hour: next });
      if (!useStore.getState().reminder.on) return;
      setError(null);
      try {
        const sub = await currentSubscription();
        if (sub) await checkIn(sub, next);
      } catch (e) {
        setError(message(e));
      }
    },
    [setReminder],
  );

  const sendTest = useCallback(async () => {
    setTestState("sending");
    setError(null);
    try {
      const sub = await currentSubscription();
      if (!sub) throw new Error("This device isn't signed up yet.");
      await pushApi.test(sub.endpoint);
      setTestState("sent");
      setTimeout(() => setTestState("idle"), 6000);
    } catch (e) {
      setTestState("idle");
      setError(message(e));
    }
  }, []);

  return { availability, on: on && availability === "ready", hour, busy, error, testState, turnOn, turnOff, setHour: changeHour, sendTest };
}

/**
 * Mounted once. If this device has reminders on: at launch (and whenever a game is finished) check in with the server,
 * so it knows the player's time zone, hour and last-played date; and quietly repair a subscription the browser dropped.
 * If the player has since blocked notifications, switch the setting off rather than pretend.
 */
export function useReminderSync(): void {
  const key = usePushKey();
  const on = useStore((s) => s.reminder.on);
  const lastCompletion = useStore((s) => s.streaks.currentStreakStart);
  useEffect(() => {
    if (!key || !on || !hasPushApi()) return;
    let cancelled = false;
    const id = setTimeout(async () => {
      try {
        if (permission() !== "granted") return void useStore.getState().setReminder({ on: false });
        const reg = await navigator.serviceWorker.ready;
        const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyToBytes(key) }));
        if (!cancelled) await checkIn(sub, useStore.getState().reminder.hour);
      } catch (e) {
        console.warn("Reminder check-in failed:", e);
      }
    }, 1500);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [key, on, lastCompletion]);
}
